import { TopNav } from './TopNav';

const LAST_UPDATED = 'May 7, 2026';

export function Privacy() {
  return (
    <div className="container narrow legal">
      <TopNav />
      <h1>Privacy Policy</h1>
      <p className="legal-meta">Last updated: {LAST_UPDATED}</p>

      <p>
        Vocal Cord is operated by <strong>Smotana</strong>, established in
        Canada (the "Service", "we", "us"). This Privacy Policy explains
        what personal data we collect, how we use it, who we share it with,
        and the rights you have under <strong>GDPR</strong>, <strong>UK GDPR</strong>,
        <strong> CCPA / CPRA</strong>, <strong>Canadian PIPEDA</strong>, and
        similar laws.
      </p>

      <h2>1. The short version</h2>
      <ul>
        <li>
          Vocal Cord is a <strong>message relay</strong> between AI agents
          and the humans they serve. We are not an archive.
        </li>
        <li>
          <strong>Message contents are stored for at most 7 days</strong>,
          then automatically deleted by AWS DynamoDB TTL. We do not back
          them up, mine them, train models on them, or sell them.
        </li>
        <li>
          We do not run advertising. We do not sell or share personal
          information for cross-context behavioural advertising.
        </li>
        <li>
          You can delete your account at any time. Deletion removes all
          personal data associated with you.
        </li>
        <li>
          Data is processed in the United States (AWS <code>us-east-1</code>).
          For EU/UK/Canadian users, transfers rely on Standard Contractual
          Clauses and equivalent safeguards.
        </li>
      </ul>

      <h2>2. What we collect</h2>

      <h3>2.1 Account &amp; identity</h3>
      <ul>
        <li>
          <strong>Account ID</strong> — a random 256-bit token that doubles
          as your API key. We do not link this to your real-world identity
          unless you choose to add an email below.
        </li>
        <li>
          <strong>Email address</strong> (optional) — only if you sign in
          to the dashboard or set a recovery email.
        </li>
        <li>
          <strong>Channel addresses</strong> — for each channel you connect
          (e.g. an email address, a Telegram chat ID, a Discord user ID),
          we store the channel kind, the address, and the verification
          timestamp. This is required to route messages.
        </li>
        <li>
          <strong>Stripe customer ID and subscription ID</strong> —
          if you upgrade to a paid plan. Card details and billing addresses
          are held by Stripe, not by us.
        </li>
      </ul>

      <h3>2.2 Messages</h3>
      <ul>
        <li>
          <strong>Message content</strong> — subject and body text of
          messages your agent sends or receives via Vocal Cord. Stored
          only until the row's TTL expires (default 7 days), or until
          your account is deleted, whichever comes first.
        </li>
        <li>
          <strong>Message metadata</strong> — direction, channel, address,
          timestamps, delivery status. Used to route, deduplicate, and
          thread replies. Subject to the same 7-day TTL.
        </li>
      </ul>

      <h3>2.3 Operational data</h3>
      <ul>
        <li>
          <strong>Verification codes</strong> — short-lived secrets sent
          to a channel to prove ownership. TTL: 15 minutes.
        </li>
        <li>
          <strong>WebSocket connection records</strong> — your active SDK
          connections. TTL: 70 minutes.
        </li>
        <li>
          <strong>Per-account bot tokens, webhook secrets, OAuth tokens</strong>{' '}
          — encrypted at rest in AWS Secrets Manager. Never written to
          DynamoDB in plaintext.
        </li>
        <li>
          <strong>Inbound email (raw MIME)</strong> — stored briefly in
          AWS S3 for processing. Auto-deletes after 30 days via S3
          lifecycle policy.
        </li>
        <li>
          <strong>Service logs</strong> — request paths, error codes,
          timestamps. Logs do not include message bodies.
        </li>
      </ul>

      <h2>3. Why we process this data (legal bases under GDPR)</h2>
      <ul>
        <li>
          <strong>Contract (Art. 6(1)(b))</strong> — to provide the relay
          service you signed up for.
        </li>
        <li>
          <strong>Legitimate interests (Art. 6(1)(f))</strong> — abuse
          prevention, rate limiting, security monitoring.
        </li>
        <li>
          <strong>Legal obligation (Art. 6(1)(c))</strong> — tax records
          held by Stripe; responding to lawful requests from public
          authorities.
        </li>
        <li>
          <strong>Consent (Art. 6(1)(a))</strong> — you give consent by
          connecting a channel address. You can withdraw consent at any
          time by removing the channel or deleting your account.
        </li>
      </ul>

      <h2>4. Roles under GDPR</h2>
      <p>
        For account, billing, and channel-configuration data, Vocal Cord
        is the <strong>data controller</strong>. For message content
        passing through the relay, Vocal Cord is a <strong>data processor</strong>{' '}
        acting on your instructions: we deliver what you tell us to deliver,
        store it briefly for in-flight retrieval, then purge it.
      </p>
      <p>
        If you build a product on top of Vocal Cord and process your end-
        users' personal data through us, contact{' '}
        <a href="mailto:privacy@smotana.com">privacy@smotana.com</a> for
        a Data Processing Agreement.
      </p>

      <h2>5. Sub-processors</h2>
      <p>
        We use the following sub-processors. Adding a new sub-processor
        will be reflected in this list and announced via our changelog.
      </p>

      <table className="legal-table">
        <thead>
          <tr>
            <th>Sub-processor</th>
            <th>Purpose</th>
            <th>Region</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Amazon Web Services</td>
            <td>Compute, storage, secrets, networking</td>
            <td>USA (us-east-1)</td>
          </tr>
          <tr>
            <td>Stripe</td>
            <td>Payments, subscription management, tax records</td>
            <td>Global (Stripe-managed)</td>
          </tr>
          <tr>
            <td>Channel platforms you connect</td>
            <td>
              Delivering messages to/from the platform you chose
              (e.g. Telegram, Discord, Slack, LINE,
              Matrix, Email, GitHub, etc.). Each platform has its
              own privacy policy.
            </td>
            <td>Varies by provider</td>
          </tr>
        </tbody>
      </table>

      <h2>6. Retention</h2>
      <table className="legal-table">
        <thead>
          <tr>
            <th>Data</th>
            <th>Retention</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Message content (subject, body)</td>
            <td>Up to 7 days, then auto-deleted</td>
          </tr>
          <tr>
            <td>Inbound email (raw MIME in S3)</td>
            <td>30 days, then auto-deleted</td>
          </tr>
          <tr>
            <td>Verification codes / login links</td>
            <td>15 minutes</td>
          </tr>
          <tr>
            <td>WebSocket connection records</td>
            <td>70 minutes</td>
          </tr>
          <tr>
            <td>Account, channels, billing link</td>
            <td>For the lifetime of the account</td>
          </tr>
          <tr>
            <td>Account after deletion (tombstone)</td>
            <td>30 days, then permanently erased</td>
          </tr>
          <tr>
            <td>Stripe webhook idempotency rows</td>
            <td>30 days</td>
          </tr>
        </tbody>
      </table>

      <h2>7. Your rights</h2>
      <p>Under GDPR, UK GDPR, CCPA/CPRA, and PIPEDA you have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you.</li>
        <li>Correct inaccurate or incomplete data.</li>
        <li>Delete your data (right to erasure).</li>
        <li>Restrict or object to certain processing.</li>
        <li>
          Export your data in a portable format (JSON via the dashboard or
          API).
        </li>
        <li>
          Withdraw consent at any time, without affecting the lawfulness
          of processing carried out before withdrawal.
        </li>
        <li>
          Lodge a complaint with your local supervisory authority (e.g.
          your EU member state's data-protection authority, the UK ICO,
          California Privacy Protection Agency, or the Office of the
          Privacy Commissioner of Canada).
        </li>
      </ul>
      <p>
        We do not sell or share personal information for cross-context
        behavioural advertising. There is therefore nothing to opt out of
        under CCPA's "Do Not Sell or Share" right; we have stated this
        explicitly so you don't have to ask.
      </p>
      <p>
        To exercise any right, email{' '}
        <a href="mailto:privacy@smotana.com">privacy@smotana.com</a> or
        delete your account from the dashboard. We respond within 30 days
        (extendable once by another 60 days for complex requests, with
        notice).
      </p>

      <h2>8. International transfers</h2>
      <p>
        Personal data is processed on AWS infrastructure in the
        United States (<code>us-east-1</code>). For users in the EU, UK,
        Switzerland, and Canada, transfers rely on the European
        Commission's Standard Contractual Clauses (or the equivalent UK
        IDTA / Swiss FDPIC) executed with each sub-processor. AWS is
        certified under the EU-U.S. Data Privacy Framework.
      </p>

      <h2>9. Security</h2>
      <ul>
        <li>All data in transit is encrypted with TLS 1.2+.</li>
        <li>
          Data at rest is encrypted with AWS-managed keys (DynamoDB, S3,
          Secrets Manager).
        </li>
        <li>
          Bot tokens, webhook secrets, and OAuth tokens are stored only
          in AWS Secrets Manager — never in DynamoDB plaintext.
        </li>
        <li>
          Outbound webhook URLs are validated against an SSRF allowlist
          at delivery time.
        </li>
        <li>
          Account API keys are random 256-bit tokens. We recommend
          rotating them via the dashboard if exposure is suspected.
        </li>
      </ul>

      <h2>10. Children</h2>
      <p>
        Vocal Cord is not directed at children under 16. We do not
        knowingly collect personal data from children. If you believe a
        child has used the service, contact us and we will delete the
        account.
      </p>

      <h2>11. Changes to this policy</h2>
      <p>
        We will post material changes to this page and update the
        "Last updated" date above. Significant changes (e.g. new
        sub-processors, longer retention) will also be announced via
        the dashboard.
      </p>

      <h2>12. Contact</h2>
      <p>
        Smotana — privacy office<br />
        Email: <a href="mailto:privacy@smotana.com">privacy@smotana.com</a>
      </p>

      <footer>
        <p>
          <a href="/">home</a> · <a href="/terms">terms</a>
        </p>
      </footer>
    </div>
  );
}
